<script lang="ts" setup>
import {RoleEnum} from "~/types/User";
import type {PortfolioType} from "~/types/Portfolio";

const route = useRoute();

const userStore = useUserStore();
const globalStore = useGlobalStore()
const {t} = useI18n()

const props = defineProps({
    item: {
        type: Object as PropType<PortfolioType>,
        required: true
    },
    current_specification: {
        type: Number,
        required: true,
    },
    parent_specification: {
        type: Number,
    }
})

const portfolio_photo = props.item?.photos?.[0]?.photo || null

const masterSpecificationIds = computed(() => {
    return props.item.specifications.map(item => item.documentId)
})

const specificationTags = computed(() => {
    return globalStore.specifications.filter(item => {
        return props.item.specifications.map(spec => spec.documentId).includes(item.documentId)
    }).filter(item => !!item.parent_id)
})

const isParentSpecification = computed(() => props.current_specification == props.parent_specification)

interface Schedule {
    id: number;
    mon: DaySchedule;
    tue: DaySchedule;
    wed: DaySchedule;
    thu: DaySchedule;
    fri: DaySchedule;
    sat: DaySchedule;
    sun: DaySchedule;
}

interface DaySchedule {
    id: number;
    start: string | null;
    end: string | null;
    is_holiday: boolean;
    is_all_day: boolean;
}

const workingStatus = computed(() => {
    if(!props.item?.working_days) return null
    const now = new Date()
    const days = {
        0: 'sun',
        1: 'mon',
        2: 'tue',
        3: 'wed',
        4: 'thu',
        5: 'fri',
        6: 'sat'
    }
    const currentDay = days[now.getDay()] as keyof Schedule
    const currentTime = now.toLocaleTimeString('en-US', { hour12: false })

    const todaySchedule = props.item.working_days[currentDay]

    if (todaySchedule.is_holiday) {
        return {
            variant: 'descructive',
            message: t('Not available today')
        }
    }

    if (todaySchedule.is_all_day) {
        return {
            variant: 'positive',
            message: t('Available 24/7')
        }
    }

    if (todaySchedule.start && todaySchedule.end) {
        if (currentTime >= todaySchedule.start && currentTime <= todaySchedule.end) {
            return {
                variant: 'positive',
                message: t('Available now')
            }
        } else {
            return {
                variant: 'descructive',
                message: t('Not available now')
            }
        }
    }

    return null
})

</script>

<template>
    <div class="">
        <div class="flex-row flex-wrap md:flex-nowrap rounded-2xl flex items-start justify-start">
            <div class="flex lg:flex-col flex-row w-full lg:w-auto">
                <CustomUiUserAvatar
                    :name="item.user.username"
                    :src="item.user.avatar?.formats?.small?.url"
                    class="lg:w-full cstm_style_avatar mr-4 lg:mr-0"
                    shape="square"
                    size="xl"/>

                <div class="w-full lg:hidden">
                    <h2 class="text-[16px] font-medium block">{{ item.user.username }}</h2>
                    <CustomUiRatingStars v-if="item.user.compute_rating" :rating="item.user.compute_rating" class="mt-0 lg:mt-2 lg:justify-center"/>

                    <div v-if="item.user.compute_review_count" class="flex w-full items-center lg:justify-center mt-0 lg:mt-2">
                        <Icon class="text-gray-600" name="ri:chat-heart-line" size="24"/>
                        <p class="font-medium text-sm m-1"> {{ item.user.compute_review_count }} {{ $t("reviews") }}</p>
                    </div>
                </div>
            </div>


            <div
                class="flex-grow w-full lg:w-auto flex flex-col items-start mt-4 lg:mt-0 justify-between lg:h-full lg:ltr:ml-6 rtl:mr-6">
                <div class="top_row w-full">
                    <div class="flex gap-2 justify-between">
                        <h2 class="text-md font-medium hidden lg:inline">{{ item.user.username }}</h2>
                        <div v-if="item.user.compute_rating" class="flex items-center">
                            <Icon name="ri:star-fill" size="14px"
                                  class="text-[#F2AE40] mx-1" />
                            <span class="text-gray-500 text-xs">{{ item.user.compute_rating?.toFixed(1) }}</span>
                        </div>
                    </div>
                    
                    <p v-if="item.description" class="text-sm mt-1.5 line-clamp-2 max-w-full [overflow-wrap:anywhere]">
                        {{ item.description }}
                    </p>

                    <div v-if="item.status_text" class="bg-stroke rounded-lg mt-2 p-3 text-xs text-primary-500 text-center font-bold">
                        {{ item.status_text }}
                    </div>
                    <div
                            v-else-if="workingStatus"
                            class="bg-stroke rounded-lg mt-2 p-3 text-xs  text-center font-bold"
                            :class="{
                                'text-primary-500': workingStatus.variant === 'positive',
                                'text-red-700': workingStatus.variant === 'destructive'
                            }"
                    >
                        {{ workingStatus.message }}
                    </div>
                </div>
            </div>
        </div>
        <div class="mt-3 grid grid-cols-2 w-full gap-4">
            <PagesCatalogSuggestOrder
                v-if="userStore.isAuthenticated && userStore.user?.user_role !== RoleEnum.MASTER"
                :master_id="item.documentId"
                :onlySpecificationIds="isParentSpecification ? masterSpecificationIds : [current_specification]"
                :specification_id="route.params.id"
            />

            <UiButton as-child class="border-primary-500 rounded-lg px-3 py-2 text-sm font-medium text-primary-500 hover:bg-primary-50 hover:text-primary-500 hover:border-transparent"
                      variant="outline">
                <NuxtLink :to="{name: 'portfolio', params: {id: item.documentId}, query: {mode: 'geo'}}">{{
                        $t("Portfolio")
                    }}
                </NuxtLink>
            </UiButton>
        </div>
    </div>
</template>
